import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { acceptTos } from "../lib/api";

/**
 * V2.0.20 — Shared Terms-of-Service consent checkbox.
 *
 * Drop this above any paid action (Stripe checkout button, file
 * download, IFC export) to gate the action behind a logged audit-trail
 * acceptance. The summary copy is identical across every surface so
 * legal can rely on a single source of truth.
 *
 * Behaviour:
 *   - Renders the checkbox + verbatim summary + inline link to /terms.
 *   - When the user ticks the box we POST /api/tos/accept (with the
 *     surface + optional design_id + client-computed sha256 of the
 *     summary) and surface a brief spinner.
 *   - On success the parent's `onAccepted(acceptanceId)` callback fires
 *     so the parent can enable its action button. On failure we revert
 *     the checkbox and call `onError(err)` so the parent can toast.
 *   - Unticking the box revokes the local `accepted` state immediately
 *     (the audit row stays in MongoDB — it's an event log, not a
 *     consent toggle). Re-ticking creates a fresh row.
 *
 * Props:
 *   surface     — required. One of:
 *                   "lightbox_download" |
 *                   "stripe_checkout" |
 *                   "ai_renders_checkout" |
 *                   "signoff_checkout"
 *   designId    — optional design context for the audit row
 *   onAccepted  — (acceptanceId: string) => void; called on POST success
 *   onRevoked   — () => void; called when the user un-ticks
 *   onError     — (err: Error) => void; called on POST failure
 *   testIdPrefix — optional. Defaults to "tos-consent" — used to keep
 *                  multiple instances on the same page distinct.
 *
 * The summary text MUST match the backend's TOS_SUMMARY_TEXT byte-for-byte
 * (the backend recomputes sha256 and stores `client_hash_match=true` so
 * we know the user saw the canonical copy).
 */
export const TOS_SUMMARY_TEXT =
  "I agree to the Terms of Service. I understand that Blueprint AI provides " +
  "conceptual design models only and does not provide stamped architectural " +
  "or engineering documents. I acknowledge that I must have these files " +
  "reviewed and stamped by a licensed professional in my jurisdiction prior " +
  "to permitting or construction.";

async function sha256Hex(text) {
  // Subtle is available in all modern browsers + the test runner.
  // Fall back to empty string if Subtle isn't available (e.g. SSR);
  // the backend still records the acceptance and flags client_hash_match
  // false so we know not to over-trust the row for that user.
  try {
    const enc = new TextEncoder().encode(text);
    const buf = await window.crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

export default function TosConsentCheckbox({
  surface,
  designId = null,
  onAccepted,
  onRevoked,
  onError,
  testIdPrefix = "tos-consent",
  className = "",
}) {
  const [checked, setChecked] = useState(false);
  const [posting, setPosting] = useState(false);

  const handleToggle = async (next) => {
    if (next) {
      setChecked(true);
      setPosting(true);
      try {
        const clientHash = await sha256Hex(TOS_SUMMARY_TEXT);
        const r = await acceptTos({
          surface,
          design_id: designId || undefined,
          client_summary_hash: clientHash,
        });
        onAccepted?.(r.data?.id);
      } catch (err) {
        // Roll the local state back so the user sees the failure and
        // can retry; the parent's onError handler toasts.
        setChecked(false);
        onError?.(err);
      } finally {
        setPosting(false);
      }
    } else {
      setChecked(false);
      onRevoked?.();
    }
  };

  return (
    <label
      htmlFor={`${testIdPrefix}-checkbox`}
      data-testid={`${testIdPrefix}-row`}
      className={`flex items-start gap-2 cursor-pointer select-none group ${className}`}
    >
      <input
        id={`${testIdPrefix}-checkbox`}
        type="checkbox"
        checked={checked}
        disabled={posting}
        onChange={(e) => handleToggle(e.target.checked)}
        data-testid={`${testIdPrefix}-checkbox`}
        className="mt-0.5 h-4 w-4 accent-brass-500 cursor-pointer disabled:cursor-wait"
      />
      <span className="text-[11px] leading-snug text-zinc-300 group-hover:text-zinc-100 transition-colors flex-1">
        I agree to the{" "}
        <a
          href="/terms"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-terms-link`}
          className="text-brass-400 hover:text-brass-300 underline underline-offset-2"
        >
          Terms of Service
        </a>
        . I understand that Blueprint AI provides{" "}
        <strong className="text-zinc-100">conceptual design models only</strong>{" "}
        and does not provide stamped architectural or engineering documents. I
        acknowledge that I must have these files reviewed and stamped by a
        licensed professional in my jurisdiction prior to permitting or
        construction.
        {posting && (
          <span
            data-testid={`${testIdPrefix}-spinner`}
            className="inline-flex items-center gap-1 ml-1 text-brass-400"
          >
            <Loader2 className="w-3 h-3 animate-spin" />
            recording…
          </span>
        )}
      </span>
    </label>
  );
}
